/**
 * chunkReload — recover from stale-chunk dynamic-import failures (NEVER-DARK §A).
 *
 * Root cause: this PWA code-splits routes; every deploy re-hashes chunk
 * filenames. A client that loaded index.html BEFORE a deploy holds a module
 * graph pointing at the OLD chunk URLs, so the next lazy-route navigation
 * requests a chunk the origin no longer serves (and that CacheFirst /assets
 * may also lack) → the dynamic import() rejects, Vite fires `vite:preloadError`,
 * and the route never mounts → DARK screen. The fix: detect that signature and
 * reload ONCE so the fresh index.html + chunk graph are fetched.
 *
 * Loop hazard: a naive "reload on chunk error" bricks the other way — if the
 * reload can't recover (origin truly missing the asset, hard offline, a
 * misclassified error) the page reloads forever. `decideReload` is the
 * loop-guard: it records the last reload in sessionStorage (survives reloads,
 * dies with the tab) and refuses to reload again inside a 10s window, surfacing
 * a static banner instead — never dark, never a reload storm.
 *
 * Intentionally FRAMEWORK-FREE: it must run when the React tree failed to
 * mount. All side effects (reload, surface, storage, time) are injected so the
 * decision logic is pure and testable WITHOUT touching real `location.reload`.
 */

/** sessionStorage key holding the epoch-ms timestamp of our last self-reload. */
export const RELOAD_AT_KEY = 'appic:chunk-reload-at'

/**
 * Minimum gap between two automatic reloads. A genuine stale-chunk reload
 * recovers well within this window; a second failure inside it means the
 * reload did NOT fix anything → stop looping and surface the error instead.
 */
export const RELOAD_LOOP_WINDOW_MS = 10_000

/** Outcome of the loop-guard decision. */
export type ReloadDecision = 'reload' | 'surface-error'

/**
 * Pure loop-guard. Given the current time and the timestamp of the last
 * automatic reload (or null/NaN if none / unreadable), decide whether to
 * reload again or to stop and surface the error.
 *
 * - no prior reload (null)               → reload
 * - prior reload OLDER than the window    → reload (a fresh, unrelated failure)
 * - prior reload INSIDE the window        → surface-error (we just reloaded; looping)
 * - unparseable prior value (NaN)         → reload (treat as "no record")
 */
export function decideReload(now: number, lastReloadAt: number | null): ReloadDecision {
  if (lastReloadAt === null || Number.isNaN(lastReloadAt)) return 'reload'
  if (now - lastReloadAt >= RELOAD_LOOP_WINDOW_MS) return 'reload'
  return 'surface-error'
}

/** Null-safe read of the last-reload timestamp from session storage. */
export function readLastReloadAt(storage: Storage | undefined = safeSessionStorage()): number | null {
  if (!storage) return null
  let raw: string | null
  try {
    raw = storage.getItem(RELOAD_AT_KEY)
  } catch {
    // Storage access can throw (privacy mode, disabled cookies). Treat as absent.
    return null
  }
  if (raw === null) return null
  const n = Number(raw)
  return Number.isNaN(n) ? null : n
}

/** Null-safe write of the last-reload timestamp to session storage. */
export function writeLastReloadAt(value: number, storage: Storage | undefined = safeSessionStorage()): void {
  if (!storage) return
  try {
    storage.setItem(RELOAD_AT_KEY, String(value))
  } catch {
    // Storage write can throw (quota, privacy mode). Best-effort only.
  }
}

/** Resolve sessionStorage without throwing in non-browser / locked-down contexts. */
function safeSessionStorage(): Storage | undefined {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage : undefined
  } catch {
    return undefined
  }
}

/** Injectable collaborators so the recovery logic is fully unit-testable. */
export interface RecoverDeps {
  /** Current epoch-ms. Injected for deterministic tests. */
  now: () => number
  /** Read the last automatic-reload timestamp. */
  readLastReloadAt: () => number | null
  /** Persist a new automatic-reload timestamp. */
  writeLastReloadAt: (value: number) => void
  /** Reload the page. NEVER the real location.reload in tests. */
  reload: () => void
  /** Surface a static error to the user (loop-guard tripped). */
  surfaceError: (reason: string, err?: unknown) => void
}

/** Build the default, real-environment dependency set. */
export function defaultRecoverDeps(): RecoverDeps {
  return {
    now: () => Date.now(),
    readLastReloadAt: () => readLastReloadAt(),
    writeLastReloadAt: (value: number) => writeLastReloadAt(value),
    reload: () => {
      if (typeof window !== 'undefined' && window.location) window.location.reload()
    },
    surfaceError: (reason: string, err?: unknown) => defaultSurfaceError(reason, err),
  }
}

/**
 * Recover from a stale-chunk failure: consult the loop-guard, then either
 * record-the-time-and-reload, or surface the error if we just reloaded.
 *
 * @returns the decision taken (handy for tests / callers).
 */
export function recoverFromStaleChunk(deps: RecoverDeps, reason: string, err?: unknown): ReloadDecision {
  const now = deps.now()
  const decision = decideReload(now, deps.readLastReloadAt())
  if (decision === 'reload') {
    deps.writeLastReloadAt(now)
    deps.reload()
  } else {
    deps.surfaceError(reason, err)
  }
  return decision
}

/**
 * Wire `window` 'vite:preloadError' → recoverFromStaleChunk. Vite dispatches
 * this CustomEvent when a dynamic-import preload fails (the stale-chunk
 * signature). Calling `preventDefault()` tells Vite NOT to rethrow, since we
 * are handling recovery ourselves.
 *
 * @returns a teardown function (removes the listener).
 */
export function installPreloadErrorHandler(deps: RecoverDeps = defaultRecoverDeps()): () => void {
  if (typeof window === 'undefined') return () => {}
  const handler = (event: Event) => {
    const err = (event as unknown as { payload?: unknown }).payload
    if (typeof (event as Event).preventDefault === 'function') event.preventDefault()
    recoverFromStaleChunk(deps, 'vite:preloadError', err)
  }
  window.addEventListener('vite:preloadError', handler as EventListener)
  return () => window.removeEventListener('vite:preloadError', handler as EventListener)
}

/** Injectable collaborators for the service-worker controllerchange reload. */
export interface SwUpdateReloadDeps {
  serviceWorker: ServiceWorkerContainer | undefined
  /** True when the page is already controlled by a SW at install time. */
  hasController: () => boolean
  reload: () => void
  readLastReloadAt: () => number | null
  writeLastReloadAt: (value: number) => void
  now: () => number
  surfaceError: (reason: string, err?: unknown) => void
}

/** Build the default SW-reload dependency set from the live navigator. */
export function defaultSwUpdateReloadDeps(): SwUpdateReloadDeps {
  const sw =
    typeof navigator !== 'undefined' && 'serviceWorker' in navigator
      ? navigator.serviceWorker
      : undefined
  return {
    serviceWorker: sw,
    hasController: () => !!(sw && sw.controller),
    reload: () => {
      if (typeof window !== 'undefined' && window.location) window.location.reload()
    },
    readLastReloadAt: () => readLastReloadAt(),
    writeLastReloadAt: (value: number) => writeLastReloadAt(value),
    now: () => Date.now(),
    surfaceError: (reason: string, err?: unknown) => defaultSurfaceError(reason, err),
  }
}

/**
 * Reload exactly once when a NEW service worker takes control, so the page
 * runs the freshly-deployed assets instead of the pre-deploy module graph.
 *
 * IGNORE the initial 'controllerchange' that fires when a SW first takes
 * control of a previously UNCONTROLLED page (first visit / hard reload): that
 * is not a deploy swap, and reloading on it would bounce every cold start.
 * We only act when the page was ALREADY controlled at install time.
 *
 * The loop-guard is shared with the preload path: a second controllerchange
 * inside the window surfaces the error instead of reloading again.
 *
 * @returns a teardown function (removes the listener).
 */
export function installSwUpdateReload(
  overrides: Partial<SwUpdateReloadDeps> = {},
): () => void {
  const deps = { ...defaultSwUpdateReloadDeps(), ...overrides }
  const sw = deps.serviceWorker
  if (!sw || typeof sw.addEventListener !== 'function') return () => {}

  // Snapshot control state at install time. An uncontrolled page taking on its
  // FIRST controller is the cold-start case we must ignore.
  const wasControlled = deps.hasController()

  const handler = () => {
    if (!wasControlled) return // initial controllerchange on a cold page → ignore
    const now = deps.now()
    const decision = decideReload(now, deps.readLastReloadAt())
    if (decision === 'reload') {
      deps.writeLastReloadAt(now)
      deps.reload()
    } else {
      deps.surfaceError('sw:controllerchange')
    }
  }
  sw.addEventListener('controllerchange', handler)
  return () => sw.removeEventListener('controllerchange', handler)
}

/**
 * Framework-free, full-screen "please reload" banner. Used only when the
 * loop-guard refuses to auto-reload — the React tree may be unmountable, so
 * this builds DOM by hand and offers a manual reload button. Idempotent: a
 * second call replaces the existing banner rather than stacking.
 */
export function defaultSurfaceError(reason: string, err?: unknown): void {
  if (typeof document === 'undefined' || !document.body) return
  const existingId = 'appic-chunk-reload-banner'
  const prior = document.getElementById(existingId)
  if (prior) prior.remove()

  const overlay = document.createElement('div')
  overlay.id = existingId
  overlay.setAttribute('role', 'alert')
  overlay.setAttribute('aria-live', 'assertive')
  overlay.style.cssText = [
    'position:fixed', 'inset:0', 'z-index:2147483647',
    'display:flex', 'align-items:center', 'justify-content:center',
    'padding:24px', 'background:#0b0b0c', 'color:#f5f5f5',
    'font-family:system-ui,-apple-system,sans-serif',
  ].join(';')

  const card = document.createElement('div')
  card.style.cssText = 'max-width:32rem;width:100%;text-align:center'

  const heading = document.createElement('h1')
  heading.style.cssText = 'font-size:1.125rem;font-weight:600;margin:0 0 0.5rem'
  heading.textContent = 'Neue Version verfügbar'

  const body = document.createElement('p')
  body.style.cssText = 'font-size:0.875rem;opacity:0.8;margin:0 0 1.25rem'
  body.textContent =
    'Die App wurde aktualisiert. Bitte laden Sie die Seite neu, um fortzufahren.'

  const button = document.createElement('button')
  button.type = 'button'
  button.textContent = 'Seite neu laden'
  button.style.cssText = [
    'display:inline-flex', 'align-items:center', 'justify-content:center',
    'border:0', 'border-radius:0.5rem', 'padding:0.5rem 1rem',
    'font-size:0.875rem', 'font-weight:500', 'cursor:pointer',
    'background:#3b82f6', 'color:#fff',
  ].join(';')
  button.addEventListener('click', () => {
    if (typeof window !== 'undefined' && window.location) window.location.reload()
  })

  card.appendChild(heading)
  card.appendChild(body)
  card.appendChild(button)
  overlay.appendChild(card)
  document.body.appendChild(overlay)

  // Best-effort diagnostic — never throw from the surface path.
  try {
    if (typeof console !== 'undefined') {
      console.warn('[chunkReload] surfaced reload banner', { reason, err })
    }
  } catch {
    // ignore
  }
}

/**
 * Bootstrap: wire BOTH recovery paths. Call once, early, from main.tsx — after
 * the global error handlers so a stale-chunk error is recovered rather than
 * merely logged.
 */
export function installChunkReloadHandlers(): void {
  installPreloadErrorHandler()
  // The SW controllerchange path is also installed from SWUpdatePrompt (where
  // the registration lives); installing here too is idempotent in practice
  // because both share the sessionStorage loop-guard. We deliberately do NOT
  // double-install it here to avoid two reloads racing — SWUpdatePrompt owns it.
}
